import { useEffect, useState, useCallback } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Loader2, Sparkles, Check, X, RefreshCw, AlertTriangle } from "lucide-react";

/**
 * AdminQuestionRepairPage — surface questions whose clinical stem was lost
 * during import. Admin can regenerate a draft vignette with the LLM,
 * edit it, and apply it. Nothing is saved without human approval.
 */
const AdminQuestionRepairPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [drafts, setDrafts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/questions/orphaned");
      setItems(r.items || []);
      const d = {};
      (r.items || []).forEach((q) => {
        if (q.repair_draft) d[q.id] = q.repair_draft;
      });
      setDrafts(d);
    } catch {
      toast.error("Failed to load orphaned questions");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setBusyFor = (id, v) => setBusy((b) => ({ ...b, [id]: v }));

  const generate = async (id) => {
    setBusyFor(id, "generate");
    try {
      const r = await api.post(`/admin/questions/${id}/generate-vignette`, {});
      setDrafts((d) => ({ ...d, [id]: r.draft }));
      toast.success("Draft generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed");
    } finally { setBusyFor(id, false); }
  };

  const apply = async (id) => {
    const vignette = (drafts[id] || "").trim();
    if (!vignette) { toast.error("Empty draft"); return; }
    setBusyFor(id, "apply");
    try {
      await api.post(`/admin/questions/${id}/apply-vignette`, { vignette });
      toast.success("Vignette applied");
      setItems((its) => its.filter((q) => q.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally { setBusyFor(id, false); }
  };

  const dismiss = async (id) => {
    setBusyFor(id, "dismiss");
    try {
      await api.post(`/admin/questions/${id}/dismiss-orphan`, {});
      toast.success("Marked as OK");
      setItems((its) => its.filter((q) => q.id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dismiss failed");
    } finally { setBusyFor(id, false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div data-testid="admin-question-repair" className="space-y-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Question repair</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Questions that look like they lost their clinical stem during import
            (&quot;this patient&quot;, &quot;how would you prevent similar…&quot;), plus any question a user reported.
            Generate a draft vignette with the LLM, edit it, and apply.
          </p>
        </div>
        <Button onClick={load} testid="repair-refresh" />
      </header>

      <div className="rounded-xl border border-warning/40 bg-warning/5 p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
        <div className="text-sm">
          <strong>{items.length}</strong> question{items.length === 1 ? "" : "s"} need review. Every draft is human-approved before it goes live.
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-muted-foreground text-sm">
          Nothing to repair. Nice.
        </div>
      ) : (
        <ul className="space-y-4">
          {items.map((q) => (
            <li key={q.id} data-testid={`repair-item-${q.id}`} className="rounded-xl border border-border bg-card p-4 space-y-3">
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="rounded-full bg-primary/10 text-primary px-2 py-0.5 font-mono">{q.subject_id}</span>
                <span className="rounded-full bg-muted px-2 py-0.5">{q.difficulty || "—"}</span>
                {q.reported_count > 0 && (
                  <span className="rounded-full bg-destructive/10 text-destructive px-2 py-0.5">
                    Reported ×{q.reported_count}
                  </span>
                )}
                {q.quality_hold && (
                  <span data-testid={`repair-hidden-${q.id}`} className="rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 font-medium">Hidden – needs review</span>
                )}
                <span className="ml-auto text-muted-foreground font-mono text-[10px]">{q.id.slice(0, 8)}</span>
              </div>

              {q.hold_reason && (
                <div data-testid={`repair-reason-${q.id}`} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <span className="font-semibold">Reason:</span> {q.hold_reason}
                </div>
              )}

              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-1">Question (as candidate sees it)</div>
                <div className="text-sm font-medium">{q.question_text}</div>
              </div>

              <ol className="text-xs text-muted-foreground space-y-0.5 pl-4 list-decimal">
                {(q.options || []).map((o) => (
                  <li key={o.id} className={o.id === q.correct_option_id ? "text-success font-medium" : ""}>
                    {o.text}{o.id === q.correct_option_id && " ✓"}
                  </li>
                ))}
              </ol>

              {q.explanation && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">Existing explanation</summary>
                  <div className="mt-1 text-muted-foreground italic">{q.explanation}</div>
                </details>
              )}

              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-1">Draft clinical vignette</div>
                <textarea
                  data-testid={`repair-draft-${q.id}`}
                  value={drafts[q.id] || ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [q.id]: e.target.value }))}
                  placeholder="Empty. Click 'Generate' to draft a vignette with the LLM, then edit."
                  rows={4}
                  className="w-full text-sm rounded-lg border border-border bg-background p-3"
                />
              </div>

              <div className="flex gap-2 flex-wrap">
                <button data-testid={`repair-generate-${q.id}`}
                        onClick={() => generate(q.id)}
                        disabled={!!busy[q.id]}
                        className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary text-xs font-semibold px-3 py-1.5">
                  {busy[q.id] === "generate" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {drafts[q.id] ? "Regenerate" : "Generate with AI"}
                </button>
                <button data-testid={`repair-apply-${q.id}`}
                        onClick={() => apply(q.id)}
                        disabled={!!busy[q.id] || !drafts[q.id]}
                        className="inline-flex items-center gap-1.5 rounded-full bg-success text-success-foreground text-xs font-semibold px-3 py-1.5 disabled:opacity-40">
                  {busy[q.id] === "apply" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                  Apply
                </button>
                <button data-testid={`repair-dismiss-${q.id}`}
                        onClick={() => dismiss(q.id)}
                        disabled={!!busy[q.id]}
                        className="inline-flex items-center gap-1.5 rounded-full bg-muted hover:bg-muted/70 text-xs font-semibold px-3 py-1.5">
                  {busy[q.id] === "dismiss" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                  Not orphaned
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Small refresh button matching the visual language of the rest of the admin
const Button = ({ onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card hover:bg-muted text-xs font-semibold px-3 py-1.5">
    <RefreshCw className="w-3.5 h-3.5" /> Refresh
  </button>
);

export default AdminQuestionRepairPage;
